/**
 * Branded ID types for @app/shared.
 *
 * Branded primitives prevent accidental cross-assignment between IDs
 * of different entity kinds at compile time, with zero runtime cost.
 * The brand symbol is a phantom property that exists only in the type
 * system; values are constructed via the asserting helpers below.
 *
 * Example: a function that takes a `ShipmentId` will not accept a
 * `LotId` even though both are strings at runtime.
 */

declare const brand: unique symbol

type Brand<TBase, TBrand extends string> = TBase & {
	readonly [brand]: TBrand
}

// --- Entity IDs --------------------------------------------------------

export type TenantId = Brand<string, "TenantId">
export type UserId = Brand<string, "UserId">
export type RoleId = Brand<string, "RoleId">
export type ShipmentId = Brand<string, "ShipmentId">
export type LotId = Brand<string, "LotId">
export type DocumentId = Brand<string, "DocumentId">
export type DocumentTypeId = Brand<string, "DocumentTypeId">
export type CorrectionRoundId = Brand<string, "CorrectionRoundId">
export type MerchantChangeId = Brand<string, "MerchantChangeId">
export type PartyId = Brand<string, "PartyId">
export type CarrierId = Brand<string, "CarrierId">
export type WarehouseId = Brand<string, "WarehouseId">
export type VerticalId = Brand<string, "VerticalId">
export type CountryVerticalId = Brand<string, "CountryVerticalId">
export type LaneId = Brand<string, "LaneId">
export type PortId = Brand<string, "PortId">
export type NotificationId = Brand<string, "NotificationId">
export type AuditLogEntryId = Brand<string, "AuditLogEntryId">
export type AttachmentId = Brand<string, "AttachmentId">

// --- Nominal codes -----------------------------------------------------
// Not opaque IDs but worth branding to prevent mixing.
// CountryCode = ISO 3166-1 alpha-2; Unlocode = 5-char UN/LOCODE port
// code; HsCode = 6-10 digit Harmonized System commodity code.

export type CountryCode = Brand<string, "CountryCode">
export type Unlocode = Brand<string, "Unlocode">
export type HsCode = Brand<string, "HsCode">

// --- Asserting constructors --------------------------------------------
// Trust-the-caller helpers used after the DB layer (or a zod parse) has
// already validated the value. For untrusted input, validate first with
// the per-entity zod schemas (gre-11/shared-zod-schemas) before branding.

export const asTenantId = (raw: string): TenantId => raw as TenantId
export const asUserId = (raw: string): UserId => raw as UserId
export const asRoleId = (raw: string): RoleId => raw as RoleId
export const asShipmentId = (raw: string): ShipmentId => raw as ShipmentId
export const asLotId = (raw: string): LotId => raw as LotId
export const asDocumentId = (raw: string): DocumentId => raw as DocumentId
export const asDocumentTypeId = (raw: string): DocumentTypeId => raw as DocumentTypeId
export const asCorrectionRoundId = (raw: string): CorrectionRoundId => raw as CorrectionRoundId
export const asMerchantChangeId = (raw: string): MerchantChangeId => raw as MerchantChangeId
export const asPartyId = (raw: string): PartyId => raw as PartyId
export const asCarrierId = (raw: string): CarrierId => raw as CarrierId
export const asWarehouseId = (raw: string): WarehouseId => raw as WarehouseId
export const asVerticalId = (raw: string): VerticalId => raw as VerticalId
export const asCountryVerticalId = (raw: string): CountryVerticalId => raw as CountryVerticalId
export const asLaneId = (raw: string): LaneId => raw as LaneId
export const asPortId = (raw: string): PortId => raw as PortId
export const asNotificationId = (raw: string): NotificationId => raw as NotificationId
export const asAuditLogEntryId = (raw: string): AuditLogEntryId => raw as AuditLogEntryId
export const asAttachmentId = (raw: string): AttachmentId => raw as AttachmentId

export const asCountryCode = (raw: string): CountryCode => raw as CountryCode
export const asUnlocode = (raw: string): Unlocode => raw as Unlocode
export const asHsCode = (raw: string): HsCode => raw as HsCode
